// src/components/Navbar.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Navbar.css";

import { auth } from "../firebaseConfig";
import { onAuthStateChanged, signOut } from "firebase/auth";

function Navbar({ cartCount = 0 }) {
  const [open, setOpen] = useState(false);          // mobile drawer
  const [panelOpen, setPanelOpen] = useState(false); // cart panel
  const [query, setQuery] = useState("");
  const [localCartCount, setLocalCartCount] = useState(0);
  const [user, setUser] = useState(null);           // 🔥 logged in user

  const navigate = useNavigate();
  const panelRef = useRef(null);

  // CART COUNT FROM LOCALSTORAGE
  useEffect(() => {
    const readCart = () => {
      try {
        const c = JSON.parse(localStorage.getItem("cart")) || [];
        const qty = c.reduce((s, it) => s + Number(it.qty || 1), 0);
        setLocalCartCount(qty);
      } catch {
        setLocalCartCount(0);
      }
    };

    readCart();

    const onStorage = (e) => {
      if (e.key === "cart" || e.key === null) readCart();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // AUTH LISTENER – user login / logout
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
    });
    return () => unsub();
  }, []);

  // close cart panel when clicking outside
  useEffect(() => {
    const handleDoc = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleDoc);
    return () => document.removeEventListener("mousedown", handleDoc);
  }, []);

  const submitSearch = (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (q) navigate(`/products?search=${encodeURIComponent(q)}`);
    else navigate("/products");
    setQuery("");
    setOpen(false);
  };

  const effectiveCount = cartCount || localCartCount || 0;

  // LOGOUT (sirf drawer se call hoga)
  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.error("Logout failed", e);
    }
    setOpen(false);
    setPanelOpen(false);
    navigate("/login");
  };

  return (
    <header className="kw-navbar simple-top">
      <div className="kw-container">
        {/* Left: Hamburger + Brand */}
        <div
          className="left-block"
          style={{ display: "flex", alignItems: "center", gap: 10 }}
        >
          <button
            className={`kw-hamburger ${open ? "open" : ""}`}
            onClick={() => setOpen((s) => !s)}
            aria-label="Toggle navigation"
          >
            <span />
            <span />
            <span />
          </button>

          <Link
            to="/products"
            className="kw-brand"
            onClick={() => setOpen(false)}
          >
            <span className="kw-logo">KW</span>
            <span className="kw-title">Kwicth Store</span>
          </Link>
        </div>

        {/* Center: Search */}
        <form
          className="kw-search center"
          onSubmit={submitSearch}
          role="search"
          aria-label="Search products"
        >
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products..."
          />
          <button type="submit" className="icon-btn small" aria-label="Search">
            🔍
          </button>
        </form>

        {/* Right: Account + Cart */}
        <div className="kw-right simple-right">
          {/* 👤 ACCOUNT / LOGIN BUTTON – ALWAYS TO /login */}
          <button
            type="button"
            className="account-btn"
            onClick={() => navigate("/login")}
          >
            <span className="account-icon">👤</span>
            {/* user nahi hai tabhi "Login" text */}
            {!user && <span className="account-text">Login</span>}
          </button>

          {/* Cart with dropdown panel */}
          <div className="cart-wrapper" ref={panelRef}>
            <button
              className="kw-icon-btn"
              onClick={() => setPanelOpen((s) => !s)}
              aria-label="Cart"
            >
              🛍️
              {effectiveCount > 0 && (
                <span className="kw-cart-count">{effectiveCount}</span>
              )}
            </button>

            <div className={`cart-panel ${panelOpen ? "open" : ""}`}>
              <nav className="cart-nav">
                <button
                  className="nav-link"
                  onClick={() => {
                    setPanelOpen(false);
                    navigate("/products");
                  }}
                >
                  Products
                </button>
                <button
                  className="nav-link"
                  onClick={() => {
                    setPanelOpen(false);
                    navigate("/order");
                  }}
                >
                  Orders
                </button>
                <button
                  className="nav-link"
                  onClick={() => {
                    setPanelOpen(false);
                    navigate("/order-tracking");
                  }}
                >
                  Track Order
                </button>
              </nav>

              <div className="cart-summary">
                <div className="summary-title">Your Bag</div>
                <div className="summary-row">
                  <span>Items</span>
                  <span>{effectiveCount}</span>
                </div>

                <button
                  className="btn"
                  onClick={() => {
                    setPanelOpen(false);
                    navigate("/cart");
                  }}
                >
                  View Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      <div className={`mobile-drawer ${open ? "open" : ""}`}>
        <ul>
          <li>
            <Link to="/products" onClick={() => setOpen(false)}>
              Products
            </Link>
          </li>
          <li>
            <Link to="/order" onClick={() => setOpen(false)}>
              Orders
            </Link>
          </li>
          <li>
            <Link to="/order-tracking" onClick={() => setOpen(false)}>
              Track Order
            </Link>
          </li>
          <li>
            <Link to="/cart" onClick={() => setOpen(false)}>
              View Cart
            </Link>
          </li>

          {/* 🔥 LOGOUT sirf jab user logged in ho */}
          {user && (
            <li>
              <button className="drawer-link" onClick={handleLogout}>
                Logout
              </button>
            </li>
          )}

          {/* Policies same rakho */}
          <li className="drawer-policies">
            <Link to="/privacy-policy" onClick={() => setOpen(false)}>
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" onClick={() => setOpen(false)}>
              Terms & Conditions
            </Link>
            <Link to="/refund-policy" onClick={() => setOpen(false)}>
              Refund Policy
            </Link>
          </li>
        </ul>
      </div>
    </header>
  );
}

export default Navbar;
